import React, { useRef, useState, useMemo } from 'react';
import { Controller, type Control, useWatch } from 'react-hook-form';
import { IconCheck, IconPhoto, IconRefresh } from '@tabler/icons-react';
import { Button } from '@/admin/components/ui/button';
import {
  CodeEditor,
  type CodeEditorHandle,
} from '@/admin/components/code-editor';
import { MDXPreview, type MDXError } from '@/admin/components/mdx-preview';
import { CollapsibleSection } from '@/admin/components/skill/collapsible-section';
import { SkillPickerDialog } from '@/admin/components/skill-picker-dialog';
import type { Skill } from '@/admin/lib/api';
import type { BlogFormData } from '@/admin/pages/blog-detail';
import type { SlashCommandItem } from '@/admin/components/slash-command/menu';

// Asset key returned from upload (just the filename part like "1234567890-image.png")
export interface UploadedAssetKey {
  key: string;
}

interface BlogContentSectionProps {
  control: Control<BlogFormData>;
  onSaveContent: () => void;
  savingContent: boolean;
  onUploadMedia: (files: File[]) => Promise<UploadedAssetKey[]>;
  uploadingMedia: boolean;
  skills: Skill[];
  loadingSkills?: boolean;
  // Assets mapping from blog: key -> signed URL for preview
  assetsMapping?: Record<string, string>;
}

export const BlogContentSection: React.FC<BlogContentSectionProps> = ({
  control,
  onSaveContent,
  savingContent,
  onUploadMedia,
  uploadingMedia,
  skills,
  loadingSkills,
  assetsMapping = {},
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [showSkillPicker, setShowSkillPicker] = useState(false);
  const [mdxError, setMdxError] = useState<MDXError | null>(null);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<CodeEditorHandle>(null);
  const pendingInsertRef = useRef<((text: string) => void) | null>(null);

  // Watch blog content for preview
  const content = useWatch({ control, name: 'content' });

  // Assets mapping for resolving keys to URLs in preview
  const combinedAssetsMapping = useMemo(
    () => ({ ...assetsMapping }),
    [assetsMapping]
  );

  // Replace asset keys in content with signed URLs for preview
  const previewContent = useMemo(() => {
    if (!content) return content;
    let result = content;
    for (const [key, url] of Object.entries(combinedAssetsMapping)) {
      // Replace src="key" with src="url" for preview
      result = result.replace(
        new RegExp(`src="${escapeRegex(key)}"`, 'g'),
        `src="${url}"`
      );
    }
    return result;
  }, [content, combinedAssetsMapping]);

  const handleMediaUpload = async (files: File[]) => {
    if (files.length === 0) return null;

    try {
      return await onUploadMedia(files);
    } catch (err) {
      console.error('Failed to handle media upload', err);
      return null;
    }
  };

  const handleSlashCommand = async (
    item: SlashCommandItem,
    insert: (text: string) => void
  ) => {
    if (item.id === 'image' || item.id === 'video') {
      // Trigger file upload
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = item.id === 'image' ? 'image/*' : 'video/*';
      input.multiple = true;
      input.onchange = async e => {
        const files = Array.from((e.target as HTMLInputElement).files || []);
        if (files.length === 0) return;

        const uploadedFiles = await handleMediaUpload(files);
        if (!uploadedFiles) return;

        const insertions: string[] = [];
        for (const file of uploadedFiles) {
          const ext = file.key.split('.').pop()?.toLowerCase();
          const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(
            ext || ''
          );
          const isVideo = ['mp4', 'mov', 'webm'].includes(ext || '');

          // Use key in content (not URL)
          if (isImage) {
            insertions.push(
              `<Image src="${file.key}" alt="${file.key.replace(/^\d+-/, '').replace(/\.[^/.]+$/, '')}" />`
            );
          } else if (isVideo) {
            insertions.push(`<Video src="${file.key}" autoPlay muted loop />`);
          }
        }

        if (insertions.length > 0) {
          insert(insertions.join('\n\n'));
        }
      };
      input.click();
    } else if (item.id === 'youtube') {
      // Prompt for YouTube URL
      const url = window.prompt('Enter YouTube URL:');
      if (url) {
        // Extract video ID from URL
        const videoId = url.match(
          /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/
        )?.[1];

        if (videoId) {
          insert(`<YouTube id="${videoId}" />`);
        } else {
          alert('Invalid YouTube URL');
        }
      }
    } else if (item.id === 'skill') {
      pendingInsertRef.current = insert;
      setShowSkillPicker(true);
    }
  };

  const handleSkillPick = (skill: Skill) => {
    if (pendingInsertRef.current) {
      pendingInsertRef.current(`<SkillCard name="${skill.name}" />`);
      pendingInsertRef.current = null;
    }
  };

  const handleEditorMediaUpload = async (files: File[]) => {
    const uploadedFiles = await handleMediaUpload(files);
    if (!uploadedFiles) return;

    const insertions: string[] = [];
    for (const file of uploadedFiles) {
      const ext = file.key.split('.').pop()?.toLowerCase();
      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(
        ext || ''
      );
      const isVideo = ['mp4', 'mov', 'webm'].includes(ext || '');

      // Use key in content (not URL)
      if (isImage) {
        insertions.push(
          `<Image src="${file.key}" alt="${file.key.replace(/^\d+-/, '').replace(/\.[^/.]+$/, '')}" />`
        );
      } else if (isVideo) {
        insertions.push(`<Video src="${file.key}" autoPlay muted loop />`);
      }
    }

    if (insertions.length > 0 && editorRef.current) {
      editorRef.current.insertAtCursor(
        '\n\n' + insertions.join('\n\n') + '\n\n'
      );
    }
  };

  return (
    <Controller
      name="content"
      control={control}
      render={({ field }) => {
        const handleDragOver = (e: React.DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(true);
        };

        const handleDragLeave = (e: React.DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
        };

        const handleDrop = async (e: React.DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);

          const files = Array.from(e.dataTransfer.files).filter(
            file =>
              file.type.startsWith('image/') || file.type.startsWith('video/')
          );

          if (files.length > 0) {
            await handleEditorMediaUpload(files);
          }
        };

        const handlePaste = (e: React.ClipboardEvent) => {
          const items = e.clipboardData?.items;
          if (!items) return;

          const files: File[] = [];
          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.kind === 'file') {
              const file = item.getAsFile();
              if (
                file &&
                (file.type.startsWith('image/') ||
                  file.type.startsWith('video/'))
              ) {
                files.push(file);
              }
            }
          }

          if (files.length > 0) {
            e.preventDefault();
            handleEditorMediaUpload(files);
          }
        };

        return (
          <CollapsibleSection
            id="blog-content"
            title="Content"
            description="MDX content for the blog post"
            defaultOpen={true}
            headerActions={
              <>
                <input
                  type="file"
                  ref={mediaInputRef}
                  className="hidden"
                  multiple
                  accept="image/*,video/*"
                  onChange={e => {
                    if (e.target.files) {
                      handleEditorMediaUpload(Array.from(e.target.files));
                      e.target.value = '';
                    }
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={e => {
                    e.stopPropagation();
                    mediaInputRef.current?.click();
                  }}
                  disabled={uploadingMedia}
                >
                  <IconPhoto className="h-3.5 w-3.5 mr-1" />
                  Media
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={e => {
                    e.stopPropagation();
                    onSaveContent();
                  }}
                  disabled={savingContent}
                >
                  {savingContent ? (
                    <IconRefresh className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : (
                    <IconCheck className="h-3.5 w-3.5 mr-1" />
                  )}
                  Save
                </Button>
              </>
            }
          >
            <div
              className={`relative ${isDragging ? 'ring-2 ring-primary ring-inset rounded-lg' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onPaste={handlePaste}
            >
              {/* Split View: Editor | Preview */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 min-h-[500px]">
                {/* Left: Editor */}
                <div className="flex flex-col border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 border-b bg-muted/30 flex items-center justify-between">
                    <span className="text-xs font-medium">Editor</span>
                    <span className="text-[10px] text-muted-foreground">
                      MDX supported. Type / for commands.
                    </span>
                  </div>
                  <div className="flex-1 min-h-[400px]">
                    <CodeEditor
                      ref={editorRef}
                      value={field.value || ''}
                      onChange={field.onChange}
                      language="markdown"
                      className="h-full border-0 rounded-none"
                      errorLine={mdxError ? mdxError.line : null}
                      onSlashCommand={handleSlashCommand}
                    />
                  </div>
                </div>

                {/* Right: Preview */}
                <div className="flex flex-col border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 border-b bg-muted/30 flex items-center justify-between">
                    <span className="text-xs font-medium">Live Preview</span>
                    <span className="text-[10px] text-muted-foreground">
                      Real-time render
                    </span>
                  </div>
                  <div className="flex-1 overflow-y-auto p-6 prose prose-sm dark:prose-invert max-w-none min-h-[400px]">
                    {previewContent ? (
                      <MDXPreview source={previewContent} onError={setMdxError} />
                    ) : (
                      <p className="text-muted-foreground text-sm italic">
                        Start writing to see the preview...
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Drag overlay */}
              {isDragging && (
                <div className="absolute inset-0 bg-primary/10 flex items-center justify-center pointer-events-none z-10 rounded-lg">
                  <div className="bg-background rounded-lg px-6 py-4 shadow-lg border">
                    <p className="text-sm font-medium">
                      Drop image or video to upload
                    </p>
                  </div>
                </div>
              )}

              {/* Upload progress overlay */}
              {uploadingMedia && (
                <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <IconRefresh className="h-5 w-5 animate-spin" />
                    <span className="text-sm font-medium">
                      Uploading media...
                    </span>
                  </div>
                </div>
              )}

              {/* Tips */}
              <div className="mt-3 text-xs text-muted-foreground">
                <p>
                  Paste or drop images/videos to upload. Use{' '}
                  <code className="bg-muted px-1 rounded">&lt;Image /&gt;</code>
                  ,{' '}
                  <code className="bg-muted px-1 rounded">&lt;Video /&gt;</code>
                  ,{' '}
                  <code className="bg-muted px-1 rounded">
                    &lt;YouTube /&gt;
                  </code>
                  ,{' '}
                  <code className="bg-muted px-1 rounded">
                    &lt;SkillCard /&gt;
                  </code>{' '}
                  for custom components.
                </p>
              </div>
            </div>

            <SkillPickerDialog
              open={showSkillPicker}
              onOpenChange={setShowSkillPicker}
              skills={skills}
              onSelect={handleSkillPick}
              loading={loadingSkills}
            />
          </CollapsibleSection>
        );
      }}
    />
  );
};

// Helper to escape special regex characters
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
